'use client'

import { Slottable } from '@radix-ui/react-slot'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { motion } from 'framer-motion'
import * as React from 'react'

import { springGentle } from '../../animations'
import { cn } from '../../utils/cn'
import { AnimatedTabIndicatorProvider, useActiveTabIndicator } from './animatedTabIndicator'

function LineTabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <AnimatedTabIndicatorProvider>
      <TabsPrimitive.List
        data-slot="line-tabs-list"
        className={cn('gap-md border-border inline-flex w-fit items-center border-b', className)}
        {...props}
      />
    </AnimatedTabIndicatorProvider>
  )
}

function LineTabsTrigger({
  className,
  children,
  value,
  ref: externalRef,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const { layoutId, isActive, ref } = useActiveTabIndicator(externalRef)

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      value={value}
      data-slot="line-tabs-trigger"
      className={cn(
        'text-secondary-foreground relative cursor-pointer px-0 pt-1 pb-3 text-xs transition-colors',
        'hover:text-foreground',
        'data-[state=active]:text-primary',
        'disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    >
      {/* Slottable preserves Radix's single-child contract under `asChild`: the
          underline renders alongside the consumer's element, not via Children.only. */}
      <Slottable>{children}</Slottable>
      {isActive && layoutId ? (
        <motion.span
          data-slot="line-tabs-underline"
          layoutId={layoutId}
          layoutDependency={value}
          className="bg-primary absolute inset-x-0 -bottom-px h-px"
          transition={springGentle}
        />
      ) : null}
    </TabsPrimitive.Trigger>
  )
}

export { LineTabsList, LineTabsTrigger }
